import { useCallback, useEffect, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';

/**
 * Banner + acción para importar estructuras maestras desde D28D-SEED.
 * Visible en el panel Maestros cuando el gym aún no tiene programas propios.
 */
export default function GymImportStructuresPanel({ scopeGymId, onImported }) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [programsCount, setProgramsCount] = useState(0);
  const [cyclesCount, setCyclesCount] = useState(0);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadStatus = useCallback(async () => {
    if (!scopeGymId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [progRes, cycleRes] = await Promise.all([
        api.get('/programs', { params: { gym_id: scopeGymId, gym_only: 'true' } }),
        api.get('/cycles', { params: { gym_id: scopeGymId, gym_only: 'true' } }),
      ]);
      setProgramsCount((progRes.data?.data || []).length);
      setCyclesCount((cycleRes.data?.data || []).length);
    } catch {
      setProgramsCount(0);
      setCyclesCount(0);
    } finally {
      setLoading(false);
    }
  }, [scopeGymId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleImport = async () => {
    if (!scopeGymId || importing) return;
    const confirmMsg = t(
      'gym.import.confirm',
      '¿Importar programas, ciclos, rutinas, clases, galería, retos plantilla y planes desde la plantilla D28D-SEED? El contenido quedará independiente y podrás editarlo libremente.',
    );
    if (!window.confirm(confirmMsg)) return;

    setImporting(true);
    setError('');
    setMessage('');
    try {
      const res = await api.post(`/gyms/${scopeGymId}/clone-from-seed`);
      const data = res.data?.data;
      if (res.data?.skipped || data?.skipped) {
        setMessage(t('gym.import.already_done', 'La estructura maestra ya está importada en este gimnasio.'));
      } else {
        const counts = data?.counts || {};
        setMessage(t(
          'gym.import.success',
          'Estructura importada: {programs} programas, {cycles} ciclos, {routines} rutinas, {classes} clases.',
        )
          .replace('{programs}', String(counts.programs ?? '—'))
          .replace('{cycles}', String(counts.cycles ?? '—'))
          .replace('{routines}', String(counts.routines ?? '—'))
          .replace('{classes}', String(counts.live_classes ?? '—')));
        onImported?.(data);
      }
      await loadStatus();
    } catch (err) {
      setError(err.response?.data?.error || t('gym.import.error', 'No se pudo importar la estructura maestra'));
    } finally {
      setImporting(false);
    }
  };

  if (!scopeGymId || loading) return null;

  const needsImport = programsCount === 0 && cyclesCount === 0;
  const hasStructure = programsCount > 0 || cyclesCount > 0;

  return (
    <section
      className={`rounded-xl border p-4 space-y-3 ${
        needsImport
          ? 'border-lime-300 bg-lime-50/80'
          : 'border-slate-200 bg-slate-50/80'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1 min-w-0 flex-1">
          <h3 className="text-base font-semibold text-stone-900">
            {t('gym.import.title', 'Importar estructuras maestras')}
          </h3>
          <p className="text-sm text-stone-600">
            {needsImport
              ? t(
                'gym.import.hint_empty',
                'Tu gimnasio aún no tiene programas ni ciclos. Importa la plantilla D28D-SEED para empezar con programas, ciclos, rutinas, clases y retos listos para configurar.',
              )
              : t(
                'gym.import.hint_ready',
                'Tienes {programs} programa(s) y {cycles} ciclo(s) propios. Puedes importar la plantilla solo si aún no has configurado maestros (no sobrescribe estructura existente).',
              )
                .replace('{programs}', String(programsCount))
                .replace('{cycles}', String(cyclesCount))}
          </p>
        </div>
        <button
          type="button"
          className="btn-primary inline-flex items-center gap-2 shrink-0"
          disabled={importing || hasStructure}
          onClick={handleImport}
          title={hasStructure
            ? t('gym.import.disabled_hint', 'Ya hay estructura maestra en este gimnasio')
            : undefined}
        >
          {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {importing
            ? t('gym.import.loading', 'Importando…')
            : t('gym.import.action', 'Importar estructuras')}
        </button>
      </div>
      {error ? (
        <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
      ) : null}
      {message ? (
        <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">{message}</div>
      ) : null}
    </section>
  );
}
